import { useCallback, useEffect, useRef, useState } from "react";
import { CircleHelp, X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { helpFor } from "../page-help";

/** Typing "?" in a field is text, not a shortcut (the Knowledge base editor is contenteditable). */
function isTyping(target: EventTarget | null) {
  const el = target as HTMLElement | null;
  if (!el || !el.tagName) return false;
  return el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || !!el.closest?.("[contenteditable]:not([contenteditable='false'])");
}

/**
 * The "Help" button for the top bar and the portal header, plus the right-hand
 * panel it opens: what this page is for, how to use it, how to read it.
 * Content comes from page-help.ts by route. "?" opens it from anywhere.
 */
export default function PageHelpButton() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const help = helpFor(pathname);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "?" || event.metaKey || event.ctrlKey || event.altKey || event.defaultPrevented) return;
      if (isTyping(event.target)) return;
      event.preventDefault();
      setOpen(true);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // A link elsewhere in the app (or back/forward) moves on; the panel follows by closing.
  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    const el = dialog.current;
    if (!open || !el) return;
    const previous = document.activeElement as HTMLElement | null;
    // showModal keeps Tab inside the panel and makes the page behind it inert.
    if (!el.open) el.showModal();
    el.querySelector<HTMLElement>(".page-help-close")?.focus();
    return () => {
      if (el.open) el.close();
      (previous && document.contains(previous) ? previous : trigger.current)?.focus();
    };
  }, [open]);

  return (
    <>
      <button
        ref={trigger}
        type="button"
        className="page-help-button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Help for this page"
        title="Help for this page (press ?)"
        onClick={() => setOpen(true)}
      >
        <CircleHelp aria-hidden="true" />
        <span>Help</span>
      </button>
      {open && (
        <dialog
          ref={dialog}
          className="page-help-panel"
          aria-label="Help for this page"
          aria-describedby="page-help-purpose"
          onCancel={(event) => { event.preventDefault(); close(); }}
          // The dialog box is the backdrop; its content sits in .page-help-sheet.
          onClick={(event) => { if (event.target === event.currentTarget) close(); }}
        >
          <div className="page-help-sheet">
            <header className="page-help-head">
              <div>
                <p className="eyebrow">Help for this page</p>
                <h2>{help.title}</h2>
              </div>
              <button type="button" className="icon-button page-help-close" aria-label="Close help" onClick={close}>
                <X aria-hidden="true" />
              </button>
            </header>
            <div className="page-help-body">
              <p id="page-help-purpose" className="page-help-purpose">{help.purpose}</p>
              <section aria-labelledby="page-help-how">
                <h3 id="page-help-how">How to use it</h3>
                <ol className="page-help-steps">
                  {help.steps.map((step) => <li key={step}>{step}</li>)}
                </ol>
              </section>
              <section aria-labelledby="page-help-read">
                <h3 id="page-help-read">How to read it</h3>
                <ul className="page-help-list">
                  {help.reading.map((line) => <li key={line}>{line}</li>)}
                </ul>
              </section>
              {help.goodToKnow?.length ? (
                <section aria-labelledby="page-help-know" className="page-help-know">
                  <h3 id="page-help-know">Good to know</h3>
                  <ul className="page-help-list">
                    {help.goodToKnow.map((line) => <li key={line}>{line}</li>)}
                  </ul>
                </section>
              ) : null}
            </div>
            <footer className="page-help-foot">
              <span className="keyboard-hint">Press <kbd>?</kbd> on any page to open this.</span>
              <a href="mailto:support@nolimits.marketing">Still stuck? Email support</a>
            </footer>
          </div>
        </dialog>
      )}
    </>
  );
}
